/**
 * A department's desk (departments.md "a desk per hat"; CFO plan §5): what the department needs from you now, and
 * where today's time went — from what's on the device. Signals from the other suite apps (BattleBooks receipts with
 * no job, accepted bids with no job) come from the server (opsDeskSignals) and are merged in by the screen.
 * Signals are live, never copied into tasks: fix the thing and the line is gone. Pure — no I/O.
 */
import { workDepartments } from './workdept.js';

const open = (t) => t && t.status !== 'done' && !t.deletedAt;

/**
 * → [{ kind, title, detail?, taskId?, projectId? }] most urgent first:
 *   setup      setup not done on the job you're clocked into (it has a checklist today)
 *   over       a task in this department over its estimate
 *   overdue    a task in this department scheduled before today, still open
 *   receipt    (Field) a receipt snapped in the field with a line on no job
 *   jobs       today's jobs in this department (where the work is)
 */
export function deskSignals({ tasks = [], projects = [], departments = [], departmentId, clock = null, date, departmentMode = true, receipts = [], fieldDepartment = false }) {
  const w = workDepartments({ tasks, projects, departments, departmentMode });
  const mine = tasks.filter((t) => open(t) && !t.parentId && w.forTask(t)?.id === departmentId);
  const name = (pid) => projects.find((p) => p.id === pid)?.name || 'a job';
  const out = [];
  const pid = clock?.active ? clock.projectId : null;
  if (pid) {
    const setup = tasks.filter((t) => t.projectId === pid && t.bucket === 'setup' && t.bucketDate === date);
    const left = setup.filter(open).length;
    if (left) out.push({ kind: 'setup', title: `Setup on ${name(pid)}`, detail: `${left} of ${setup.length} left`, projectId: pid });
  }
  for (const t of mine) {
    if (t.bucket || !(t.estimatedMinutes > 0) || !((t.timeSpent || 0) > t.estimatedMinutes * 60000)) continue;
    out.push({ kind: 'over', title: t.title, detail: `${Math.round((t.timeSpent || 0) / 60000)} of ${t.estimatedMinutes} min`, taskId: t.id, projectId: t.projectId || null });
  }
  for (const t of mine) {
    if (t.bucket || !t.scheduledDate || t.scheduledDate >= date) continue;
    out.push({ kind: 'overdue', title: t.title, detail: `since ${t.scheduledDate}`, taskId: t.id, projectId: t.projectId || null });
  }
  // A receipt snapped in the field (BattleBooks → opsReceipts) with a line on no job: assign it so the job's costs
  // are right. Field's desk only — that's who snaps them.
  if (fieldDepartment) {
    for (const r of receipts || []) {
      if (!r || r.status === 'void' || r.status === 'removed') continue;
      const pages = Object.values(r.pages || {}).filter((p) => p && p.status !== 'void');
      const loose = pages.some((p) => !(p.lines || []).length || (p.lines || []).some((l) => !l.projectId));
      if (!loose) continue;
      const vendor = pages.find((p) => p.vendorName)?.vendorName || 'A receipt';
      out.push({ kind: 'receipt', title: `${vendor} — no job`, detail: r.total ? `$${Number(r.total).toFixed(2)}` : null, receiptId: r.id || r.captureId || null });
    }
  }
  const jobs = new Set(mine.filter((t) => t.projectId && (t.scheduledDate === date || t.projectId === pid)).map((t) => t.projectId));
  for (const j of jobs) out.push({ kind: 'jobs', title: name(j), detail: `${mine.filter((t) => t.projectId === j && t.scheduledDate === date).length} on today`, projectId: j });
  return out;
}

/** Today's time in this department: by job, and the quick-task / checklist share (setup, cleanup, travel…). */
export function deskTime({ tasks = [], projects = [], departments = [], departmentId, date, departmentMode = true }) {
  const w = workDepartments({ tasks, projects, departments, departmentMode });
  const byJob = new Map();
  const byQuick = new Map();
  let total = 0;
  for (const t of tasks) {
    for (const e of t.timeEntries || []) {
      if (e.date !== date || !(e.duration > 0) || w.forEntry(e, t)?.id !== departmentId) continue;
      total += e.duration;
      const k = t.projectId || 'none';
      byJob.set(k, (byJob.get(k) || 0) + e.duration);
      const q = t.quickTaskId || (t.bucket ? t.bucket : null);
      if (q) byQuick.set(q, (byQuick.get(q) || 0) + e.duration);
    }
  }
  const name = (pid) => projects.find((p) => p.id === pid)?.name || 'No job';
  return {
    totalMs: total,
    jobs: [...byJob].map(([projectId, ms]) => ({ projectId: projectId === 'none' ? null : projectId, name: name(projectId), ms })).sort((a, b) => b.ms - a.ms),
    quick: [...byQuick].map(([id, ms]) => ({ id, ms })).sort((a, b) => b.ms - a.ms),
  };
}
